
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/utils/toast';
import AuthForm from '@/components/AuthForm';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { MessageSquare } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

type AuthMode = 'login' | 'signup';

const Auth: React.FC<{ mode: AuthMode }> = ({ mode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isSignUp, setIsSignUp] = useState(location.pathname === '/signup');
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isMobile = useIsMobile();

  useEffect(() => {
    // Update tab based on URL path
    setIsSignUp(location.pathname === '/signup');
  }, [location.pathname]);

  useEffect(() => {
    // Redirect if already logged in
    if (currentUser) {
      navigate('/profile');
    }
  }, [currentUser, navigate]);

  const handleTabChange = (value: string) => {
    if (value === 'signup' && location.pathname !== '/signup') {
      navigate('/signup');
    } else if (value === 'login' && location.pathname !== '/login') {
      navigate('/login');
    }
  };

  // If user is logged in, don't render the login page
  if (currentUser) {
    return null;
  }

  return (
    <div className={cn(
      "flex items-center justify-center min-h-[calc(100vh-180px)] py-8 px-4 sm:px-6 lg:px-8 relative overflow-hidden",
      isDark ? "bg-[#0a0e1e]" : "bg-[#F5F5F7]"
    )}>
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: isMobile ? 4 : 8 }).map((_, i) => (
          <motion.div 
            key={i}
            className={cn(
              "absolute rounded-full",
              isDark ? "bg-blue-500/5" : "bg-[#D3E4FD]/30",
              isMobile ? "w-[200px] h-[200px]" : "w-[300px] h-[300px]"
            )}
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              transform: 'translate(-50%, -50%)'
            }}
            animate={{
              x: [0, isMobile ? 15 : 30, 0],
              y: [0, isMobile ? 20 : 40, 0],
            }}
            transition={{
              duration: 20 + Math.random() * 10,
              repeat: Infinity,
              ease: "easeInOut",
              repeatType: "reverse",
            }}
          />
        ))}
      </div>
      
      <div className="w-full max-w-md z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-center mb-5"
        >
          <div className="flex items-center justify-center mb-4">
            <MessageSquare size={isMobile ? 32 : 40} className="text-[#0066CC] mr-2" />
            <span className={cn("font-bold text-[#0066CC]", isMobile ? "text-3xl" : "text-4xl")}>Chatrix</span>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Card className={cn(
            "backdrop-blur-sm border shadow-xl",
            isDark 
              ? "bg-[#151830]/90 border-slate-700/30 text-white" 
              : "bg-white/95 border-[#D3E4FD]/50"
          )}>
            <CardHeader className={cn("space-y-1", isMobile ? "p-4 pb-2" : "")}>
              <CardTitle className={cn(
                "font-bold text-center", 
                isDark ? "text-white" : "text-[#0066CC]",
                isMobile ? "text-xl" : "text-2xl"
              )}>
                {isSignUp ? 'Create Account' : 'Welcome Back'}
              </CardTitle>
            </CardHeader>
            
            <CardContent className={isMobile ? "p-4" : ""}>
              <Tabs 
                defaultValue={isSignUp ? 'signup' : 'login'} 
                onValueChange={handleTabChange}
                className="w-full"
              >
                <TabsList className={cn(
                  "w-full mb-5 grid grid-cols-2", 
                  isDark 
                    ? "bg-slate-800/50" 
                    : "bg-[#F5F5F7]"
                )}>
                  <TabsTrigger 
                    value="login"
                    className={cn(
                      isDark 
                        ? "data-[state=active]:bg-[#292D67] data-[state=active]:text-white" 
                        : "data-[state=active]:bg-white data-[state=active]:text-[#0066CC] data-[state=active]:shadow-sm",
                      isMobile ? "text-sm py-1.5" : ""
                    )}
                  >
                    Login
                  </TabsTrigger>
                  <TabsTrigger 
                    value="signup"
                    className={cn(
                      isDark 
                        ? "data-[state=active]:bg-[#292D67] data-[state=active]:text-white" 
                        : "data-[state=active]:bg-white data-[state=active]:text-[#0066CC] data-[state=active]:shadow-sm",
                      isMobile ? "text-sm py-1.5" : ""
                    )}
                  >
                    Sign Up
                  </TabsTrigger>
                </TabsList>
                
                {/* Login Tab Content */}
                <TabsContent value="login" className="space-y-4">
                  <AuthForm mode="login" />
                </TabsContent>
                
                {/* Sign Up Tab Content */}
                <TabsContent value="signup" className="space-y-4">
                  <AuthForm mode="signup" />
                </TabsContent>
              </Tabs>
            </CardContent>
            
            <CardFooter className={cn("flex flex-col items-center", isMobile ? "p-4 pt-0" : "mt-2")}>
              <p className={cn(
                "text-center", 
                isDark ? "text-slate-400" : "text-slate-500",
                isMobile ? "text-xs mt-3" : "text-xs mt-4"
              )}>
                By continuing, you agree to our{' '}
                <a href="/terms" className={cn(
                  "underline underline-offset-2", 
                  isDark ? "text-blue-400 hover:text-blue-300" : "text-[#0066CC] hover:text-blue-700"
                )}>
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacy" className={cn(
                  "underline underline-offset-2", 
                  isDark ? "text-blue-400 hover:text-blue-300" : "text-[#0066CC] hover:text-blue-700"
                )}>
                  Privacy Policy
                </a>
                .
              </p>
            </CardFooter>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default Auth;
